/**
 * bomberDroneConstants.ts
 * Tuning knobs for the World 2 `BomberDrone` — a slower, heavier
 * drifter with 2 HP. Split out of `spaceEnemyConstants.ts` so each
 * enemy kind owns its own constants file (Phase 5 Tech Debt — file
 * length cap split, see ROADMAP.md).
 *
 * Consumers should keep importing from `spaceEnemyConstants` — that
 * module re-exports every symbol here so the public surface is
 * unchanged.
 *
 * @module bomberDroneConstants
 */

/** Wider hitbox — a bomber is a heavier silhouette than a fighter. */
export const BOMBER_DRONE_WIDTH = 48;

/** Taller hitbox to match the wider silhouette. */
export const BOMBER_DRONE_HEIGHT = 36;

/** Two ship-projectile hits to kill, so bombers feel meaningfully tougher. */
export const BOMBER_DRONE_HP = 2;

/**
 * Leftward drift relative to world space (px / s). Half the fighter speed
 * — a slow, threatening approach is the point of this enemy type. With the
 * 80 px/s auto-scroll the apparent on-screen velocity is 140 px/s.
 */
export const BOMBER_DRONE_DRIFT_SPEED = 60;

/**
 * Vertical sine wave amplitude (px) — heavier than the fighter so the
 * bomber feels lumbering rather than nimble.
 *
 * DECISION (W2 overhaul): Added so the bomber doesn't sit in one lane
 * while the player camps below or above it.
 */
export const BOMBER_DRONE_WAVE_AMPLITUDE_PX = 36;

/** Vertical sine frequency (Hz). Slower than the fighter to match the heavier read. */
export const BOMBER_DRONE_WAVE_FREQUENCY_HZ = 0.35;

// ── Cluster bomb (dropped projectile) ─────────────────────────────────────

/** Hitbox width of a dropped cluster bomb in logical px.
 *  Larger than a laser bolt — the bomb reads as a chunky payload, not a stray
 *  pixel. */
export const BOMBER_DRONE_BOMB_WIDTH = 14;

/** Hitbox height — square hitbox for symmetric directional pickup. */
export const BOMBER_DRONE_BOMB_HEIGHT = 14;

/** Downward velocity applied at the moment of release (px / s). The bomb
 *  inherits the bomber's leftward drift via `BOMBER_DRONE_DRIFT_SPEED`,
 *  giving the dropped payload a south-by-southwest arc that reads as
 *  "thrown forward and down" rather than a pure plummet.
 *  DECISION: Faster than the bomber's own 60 px/s drift so the bomb
 *  visibly separates from its parent within ~0.2 s; slower than the
 *  fighter laser's 320 px/s so the player can react during the fuse. */
export const BOMBER_DRONE_BOMB_DROP_VY_PX_S = 80;

/** Seconds between release and detonation. The bomb keeps drifting
 *  during this window; on expiry it spawns
 *  {@link BOMBER_DRONE_SHRAPNEL_COUNT} shrapnel pieces and dies.
 *  DECISION: Tuned so a bomb released near the bomber's spawn margin
 *  detonates roughly centre-stage, where the ship is most likely to be. */
export const BOMBER_DRONE_BOMB_FUSE_S = 1.4;

/** Seconds between consecutive bomb drops from the same bomber.
 *  DECISION: Slower than the fighter's 1.8 s laser cadence — each drop
 *  is a 6-shrapnel burst, so the per-bomber threat density is already
 *  much higher per shot. */
export const BOMBER_DRONE_DROP_INTERVAL_S = 3.0;

/** Seconds from spawn to the first drop. A short delay so a bomber
 *  drifting past the spawn margin doesn't drop a bomb in the wings
 *  before it's on-screen. */
export const BOMBER_DRONE_DROP_INITIAL_DELAY_S = 1.0;

/** Distance (px) past the camera's left edge a bomb keeps integrating
 *  before being culled. Generous so a bomb that detonates near the
 *  left margin can still spawn shrapnel that drifts back into view. */
export const BOMBER_DRONE_BOMB_OFFSCREEN_MARGIN_PX = 32;

/** Pool capacity for the bomb projectile pool.
 *  Sized as {@link SPACE_ENEMY_MAX_ACTIVE} (= 12 bombers max on-screen)
 *  so an unlucky tick where every bomber drops simultaneously still
 *  finds a slot. Realistic peak with the 3.0 s cadence is ~3-4
 *  in-flight bombs; the headroom keeps pool exhaustion off the table. */
export const BOMBER_DRONE_BOMB_POOL_CAP = 12;

// ── Shrapnel (post-fuse radial pieces) ────────────────────────────────────

/** Hitbox width of a shrapnel piece in logical px.
 *  Smaller than the bomb so the after-image reads as fragments. */
export const BOMBER_DRONE_SHRAPNEL_WIDTH = 6;

/** Hitbox height — square. */
export const BOMBER_DRONE_SHRAPNEL_HEIGHT = 6;

/** Linear speed of every shrapnel piece (px / s). Each piece flies in
 *  one of {@link BOMBER_DRONE_SHRAPNEL_COUNT} evenly-spaced headings
 *  around 2π. */
export const BOMBER_DRONE_SHRAPNEL_SPEED_PX_S = 220;

/** Number of shrapnel pieces released per detonation.
 *  DECISION: 6 = 60° between adjacent pieces. Enough density that
 *  standing dead-still gets punished, sparse enough that the player
 *  can read individual rays and dodge between them. */
export const BOMBER_DRONE_SHRAPNEL_COUNT = 6;

/** Seconds a shrapnel piece lives before self-destructing.
 *  At {@link BOMBER_DRONE_SHRAPNEL_SPEED_PX_S} = 220 px/s, this gives
 *  each piece roughly 154 px of travel — well past the bomber's drop
 *  zone but short of clearing the viewport, so detonations stay local. */
export const BOMBER_DRONE_SHRAPNEL_TTL_S = 0.7;

/** Pool capacity for shrapnel.
 *  6 pieces × ~8 in-flight bombs = 48, just under the AGENTS.md cap of
 *  50 on pooled hazard objects. Realistic concurrent live count is far
 *  lower (TTL is short, drops are spaced) — this is the worst-case
 *  envelope. */
export const BOMBER_DRONE_SHRAPNEL_POOL_CAP = 48;
